import { useEffect, useRef, useState } from 'react';
import PanelCard from 'components/card/PanelCard';
import useModal from 'components/modal/useModal';
import { companyPaymentTermLabel } from 'constants/paymentTerms';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import { Field, PlainField, TWO_COLUMN_GRID } from './overviewLayout';

export const BILLING_INFORMATION_UPDATED =
  'company_billing_information_updated';

/** "12 rue Lamartine, 97200 Fort-de-France", or an empty string. */
export const formatAddressLine = (address) =>
  address
    ? [
        address.street,
        [address.postcode, address.city].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

/**
 * Billing address and payment term of the company (JBWEB-1268, mockup 8.1).
 * Reads its own data: the company endpoint rebuilds the company field by field
 * and carries neither, and the card is the only reader.
 */
const CompanyBillingInformationCard = ({
  idCompany,
  canEdit,
  refreshKey = 0,
}) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle } = useModal();
  const [billing, setBilling] = useState(null);
  const [status, setStatus] = useState('loading');
  // The company whose information is on screen: read again (`refreshKey`), it
  // stays there while the new answer comes, and a failure leaves it.
  const loadedCompany = useRef(null);
  const text = (key, fallback) =>
    language.getValue(`companyBilling.${key}`, fallback);

  useEffect(() => {
    if (!idCompany) return undefined;
    // A late answer for a company the panel has since left is dropped.
    let isCurrent = true;
    const isRefresh = loadedCompany.current === idCompany;
    if (!isRefresh) setStatus('loading');
    ApiDatabase.getCompanyBillingInformation(
      { idCompany },
      (response) => {
        if (!isCurrent) return;
        loadedCompany.current = idCompany;
        setBilling(response?.data ?? null);
        setStatus('ready');
      },
      (error) => {
        if (!isCurrent || error?.name === 'CanceledError' || isRefresh) return;
        setStatus('error');
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [idCompany, refreshKey]);

  useEffect(() => {
    if (modalDataReturn?.type !== BILLING_INFORMATION_UPDATED) return;
    setBilling(modalDataReturn.billingInformation ?? null);
    setStatus('ready');
    setModalDataReturn({});
  }, [modalDataReturn]);

  const openEditor = () => {
    setModalData({ idCompany, billingInformation: billing });
    toggle('company_billing_information');
  };

  const notSet = text('notSet', 'Non renseigné');
  // A dash while it loads: "Non renseigné" would read as a settled answer.
  const shown = (value) => (status === 'loading' ? '—' : value || notSet);

  return (
    <PanelCard
      title={text('cardTitle', 'Informations de facturation')}
      collapsible
      onEdit={canEdit && status === 'ready' ? openEditor : null}
      contentClassName={status === 'error' ? 'px-4 py-3' : TWO_COLUMN_GRID}
    >
      {status === 'loading' ? (
        // The two cells, their labels and a line where each value will be.
        <>
          {[text('billingAddress', 'Adresse de facturation'), text('paymentTerm', 'Délai de paiement')].map((label) => (
            <div key={label} aria-busy="true" className={'p-4 min-w-0 flex flex-col gap-2'}>
              <div>{label}</div>
              <SkeletonLine className="w-40" />
            </div>
          ))}
        </>
      ) : status === 'error' ? (
        <div className={'text-red-500'}>
          {text(
            'loadError',
            'Les informations de facturation n’ont pas pu être chargées.',
          )}
        </div>
      ) : (
        <>
          <Field
            label={text('billingAddress', 'Adresse de facturation')}
            value={shown(formatAddressLine(billing?.billingAddress))}
          />
          <PlainField
            label={text('paymentTerm', 'Délai de paiement')}
            value={shown(
              companyPaymentTermLabel(billing?.paymentTerm, language),
            )}
          />
        </>
      )}
    </PanelCard>
  );
};

export default CompanyBillingInformationCard;
